import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative group overflow-hidden bg-slate-900">
        <img src="https://images.unsplash.com/photo-1762529716272-b316f61502e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100" alt="" className="absolute inset-0 w-full h-full object-cover zoom-slow opacity-90" />
        <div className="absolute inset-0 bg-slate-900/30" />
        <Link to="/" data-testid="login-brand-link" className="absolute top-10 left-10 h-20 w-48 bg-white p-3 shadow-lg">
          <BrandLogo location="login" className="invert" />
        </Link>
        <span className="absolute bottom-10 left-10 text-[11px] font-mono uppercase tracking-[0.25em] text-slate-200">Member Access</span>
      </div>
      <div className="flex items-center justify-center px-6 py-16 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <Link to="/" data-testid="login-mobile-brand-link" className="lg:hidden block mb-8 h-16 w-40"><BrandLogo location="login-mobile" className="invert" /></Link>
          <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-slate-500">Sign in</span>
          <h1 className="font-display text-5xl tracking-tight mt-3">Welcome back.</h1>
          <p className="text-slate-500 mt-3 text-sm">Access your bookings and delivered media.</p>

          {error && <div data-testid="login-error" className="mt-6 text-sm text-destructive bg-destructive/5 border border-destructive/20 px-4 py-3">{error}</div>}

          <div className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 rounded-none border-slate-300" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Password</Label>
              <div className="relative mt-2">
                <Input id="password" data-testid="login-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required className="rounded-none border-slate-300 pr-12" />
                <button
                  type="button"
                  data-testid="login-password-visibility-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-500 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-900"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full mt-8 rounded-none bg-slate-900 hover:bg-slate-800 text-slate-50 py-6">
            {loading ? "Signing in..." : "Sign in"}
          </Button>
          <p className="text-sm text-slate-500 mt-6 text-center">
            New here? <Link to="/signup" data-testid="login-to-signup" className="text-foreground underline underline-offset-4">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
